import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { MANAGEMENT_URL } from "@/config/env";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthCard } from "@/components/AuthCard";

interface LocationState {
  from?: string;
}

interface LoginResponse {
  accessToken: string;
  expiresAt: string;
  user: { id: string; email: string; role: string };
}

function isValidLoginResponse(value: unknown): value is LoginResponse {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.accessToken !== "string" || v.accessToken.length === 0) return false;
  if (typeof v.expiresAt !== "string") return false;
  if (!Number.isFinite(new Date(v.expiresAt).getTime())) return false;
  if (typeof v.user !== "object" || v.user === null) return false;
  const u = v.user as Record<string, unknown>;
  return typeof u.id === "string" && typeof u.email === "string" && typeof u.role === "string";
}

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState)?.from ?? "/";

  // #216 (D2b): Frisches System ohne Admin → ab zur Ersteinrichtung statt Login.
  useEffect(() => {
    let active = true;
    fetch(`${MANAGEMENT_URL}/auth/status`)
      .then((r) => r.json())
      .then((d: unknown) => {
        if (!active) return;
        const initialized = typeof d === "object" && d !== null && (d as { initialized?: boolean }).initialized;
        if (initialized === false) navigate("/setup", { replace: true });
      })
      .catch(() => {
        /* Master nicht erreichbar → der Login-Submit zeigt den Fehler */
      });
    return () => {
      active = false;
    };
  }, [navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const res = await fetch(`${MANAGEMENT_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (res.status === 401) {
        setError("Invalid email or password");
        return;
      }
      if (!res.ok) {
        setError(`Login failed (HTTP ${res.status})`);
        return;
      }

      const data: unknown = await res.json();
      if (!isValidLoginResponse(data)) {
        setError("Login response schema invalid (server bug?)");
        return;
      }
      const expiresAtMs = new Date(data.expiresAt).getTime();
      if (expiresAtMs <= Date.now()) {
        setError("Login token already expired (server clock skew?)");
        return;
      }
      setSession({
        accessToken: data.accessToken,
        expiresAt: expiresAtMs,
        user: data.user,
      });
      // #295: Nicht-Admins (plugin-author) starten in ihrem Arbeitsbereich.
      navigate(data.user?.role === "admin" ? from : "/plugins", { replace: true });
    } catch (err) {
      setError(`Cannot reach server: ${(err as Error).message}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthCard title="Elyvers" description="Admin-Konsole — bitte anmelden.">
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isSubmitting}
                data-testid="password-input"
              />
            </div>
            {error && (
              <div
                className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger"
                role="alert"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="submit-button">
              {isSubmitting ? "Logging in…" : "Login"}
            </Button>
          </form>
    </AuthCard>
  );
}
